import { z } from 'zod';

/**
 * Primitives and envelopes shared by every endpoint.
 *
 * These are imported by the backend (to validate requests and shape responses), by the
 * frontend (to type the client and parse responses in development) and by the mock
 * server (to generate fixtures). One definition, three consumers — which is what stops
 * the API contract drifting while the frontend is built ahead of it.
 */

// ── Primitives ──────────────────────────────────────────────────────────────────

/** A 24-character hex Mongo ObjectId. */
export const objectId = z
  .string()
  .regex(/^[0-9a-fA-F]{24}$/, 'Must be a valid id');

/**
 * A CALENDAR DAY in the organization's timezone, `YYYY-MM-DD`.
 *
 * Deliberately not a Date: a calendar day is not an instant, and storing one as a Date
 * forces a timezone choice at the boundary that every later reader has to guess at.
 * See docs/DATABASE_DESIGN.md §1.
 */
export const dayString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be a date in YYYY-MM-DD format')
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Must be a real calendar date');

/** An INSTANT, ISO 8601 in UTC. */
export const isoDateTime = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Must be an ISO 8601 date-time');

export const email = z.string().email('Must be a valid email address').toLowerCase();

/** Minimum 8 characters, no composition rules — see OPEN_QUESTIONS Q-05. */
export const password = z.string().min(8, 'Must be at least 8 characters');

/** A client-generated UUID that makes a write safe to retry. */
export const idempotencyKey = z.string().uuid('Must be a UUID');

export const nonEmpty = (label: string, max = 200) =>
  z.string().trim().min(1, `${label} is required`).max(max, `${label} is too long`);

/** Server-sanitized rich text. Never rendered without the server having cleaned it. */
export const richText = z.string().max(20_000);

// ── Query parameters ────────────────────────────────────────────────────────────

export const PAGE_SIZES = [25, 50, 100] as const;

/**
 * Standard list query. `coerce` because query strings arrive as text.
 *
 * `pageSize` is capped: an uncapped limit is how a list endpoint becomes an accidental
 * bulk-export of the whole collection.
 */
export const listQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** `field` or `-field` for descending. */
  sort: z.string().max(60).optional(),
  /** Free-text search. Never compiled into a RegExp server-side. */
  q: z.string().trim().max(120).optional(),
});

export type ListQuery = z.infer<typeof listQuery>;

/** Extend the standard list query with per-endpoint filters. */
export const listQueryWith = <T extends z.ZodRawShape>(shape: T) => listQuery.extend(shape);

export const dateRangeQuery = z.object({
  from: dayString.optional(),
  to: dayString.optional(),
});

/**
 * Data scope requested by the caller. The server NARROWS this to the widest scope the
 * actor actually holds — it never widens, so passing `organization` as an employee
 * yields own-scope results rather than an error.
 */
export const scopeQuery = z.object({
  scope: z.enum(['own', 'reports', 'department', 'organization']).optional(),
});

export const idParam = z.object({ id: objectId });

// ── Response envelopes ──────────────────────────────────────────────────────────

/** `{ data: T }` — single resource, and the shape every write returns. */
export const single = <T extends z.ZodTypeAny>(item: T) => z.object({ data: item });

export const pagination = z.object({
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
});

/** `{ data: T[], pagination }` — the one list shape. No endpoint returns a bare array. */
export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ data: z.array(item), pagination });

/** For small, inherently bounded collections (leave types, holidays in a year). */
export const collection = <T extends z.ZodTypeAny>(item: T) => z.object({ data: z.array(item) });

export const ok = z.object({ data: z.object({ ok: z.literal(true) }) });

export const empty = z.object({}).describe('204 No Content');

// ── Shared fragments ────────────────────────────────────────────────────────────

/**
 * A person as referenced from another record. Deliberately minimal: enough to render a
 * row without leaking the full employee document into every response.
 */
export const employeeRef = z.object({
  id: objectId,
  fullName: z.string(),
  employeeCode: z.string(),
  jobTitle: z.string().nullable(),
  departmentName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});
export type EmployeeRef = z.infer<typeof employeeRef>;

export const departmentRef = z.object({
  id: objectId,
  name: z.string(),
});

/** Who did what, when — attached to records that carry a decision. */
export const decisionMeta = z.object({
  decidedAt: isoDateTime.nullable(),
  decidedBy: employeeRef.nullable(),
  comment: z.string().nullable(),
});

export const timestamps = z.object({
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});

/**
 * A figure returned to a dashboard.
 *
 * `basis` is required when `kind` is `derived`, so an estimate cannot reach the UI
 * without an explanation of how it was produced. Presenting a calculation as a
 * measurement is forbidden by the product principles; this type makes that structural
 * rather than a convention someone has to remember.
 */
export const metric = z.object({
  value: z.number(),
  kind: z.enum(['observed', 'derived']),
  basis: z.string().nullable(),
  unit: z.string().nullable(),
});
export type Metric = z.infer<typeof metric>;
